/**
 * ✓ / ✓✓ / ✓✓ filled — V6-05 §4.
 *
 * Derived on the server from everyone's position in the conversation, so this
 * only renders what it is told. In a group, delivered means *all* have it and
 * read means *all* have read it: the weakest link decides, because a tick that
 * filled in when the fastest person read would be telling the sender something
 * false about everybody else.
 */

import { Check, CheckCheck } from 'lucide-react';
import { cn } from '@/lib/utils';

const LABEL: Record<string, string> = {
  sent: 'Sent',
  delivered: 'Delivered',
  read: 'Read by everyone',
};

/** "14:32" — the tooltip is about *when*, so it does not need the date. */
function at(value?: string | Date | null): string | null {
  if (!value) return null;
  return new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export function Ticks({
  tick,
  deliveredAt,
  readAt,
  onClick,
  onAccent = false,
}: {
  tick?: string | null;
  /**
   * V9-01 §4 — this tick is sitting on an own bubble, which is accent blue.
   *
   * Off by default so every light surface that renders ticks — the conversation
   * list preview, the Message info sheet — keeps the grey/grey/blue scheme
   * people already read.
   */
  onAccent?: boolean;
  /** V8-02 §2 — when it reached everybody, and when everybody read it. */
  deliveredAt?: string | Date | null;
  readAt?: string | Date | null;
  onClick?: () => void;
}) {
  if (!tick) return null;

  const Icon = tick === 'sent' ? Check : CheckCheck;

  // §2 — "tooltip with timestamps". The states are cumulative, so a read
  // message shows both times: when it landed and when it was looked at.
  const times = [
    at(deliveredAt) && `Delivered ${at(deliveredAt)}`,
    at(readAt) && `Read ${at(readAt)}`,
  ].filter(Boolean);
  const title = times.length
    ? `${times.join(' · ')} — click for details`
    : `${LABEL[tick]} — click for details`;

  return (
    <button
      type="button"
      data-testid="chat-tick"
      data-tick={tick}
      aria-label={LABEL[tick]}
      title={title}
      onClick={onClick}
      className={cn(
        'ml-1 inline-flex align-middle',
        /**
         * §2's three states, exactly: one grey ✓ sent, two grey ✓✓ delivered,
         * two blue ✓✓ read. Read is the only one that earns colour — two grey
         * ticks and two coloured ones is the distinction people already know
         * from every other messenger.
         */
        /**
         * §2's three states, on a light surface: one grey ✓ sent, two grey ✓✓
         * delivered, two blue ✓✓ read.
         *
         * On blue, colour cannot carry the distinction — §4. Sent and delivered
         * go to 70% white and read to solid white with a heavier stroke, so the
         * difference survives both a colour-blind reader and a phone in
         * sunlight. The tooltip and Message info remain the precise source;
         * these ticks were never meant to be read to the minute.
         */
        onAccent
          ? tick === 'read'
            ? 'text-[var(--tick-on-accent-read)]'
            : 'text-[var(--tick-on-accent)]'
          : tick === 'read'
            ? 'text-[var(--accent)]'
            : 'opacity-70',
      )}
    >
      <Icon className={cn('size-3', onAccent && tick === 'read' && 'stroke-[3]')} />
    </button>
  );
}
